import PropTypes from 'prop-types';
import { useContext, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { UserContext } from '../contexts/UserContext';
import { gtmEventChangePaymentInterval } from '../utils/analytics';
import { userCanPurchase } from '../utils/general';
import { getHeaderContentByStep } from '../utils/header';
import Header from './Header';
import Nav from './Nav';
import Overview from './Overview';
import Packages from './Packages';
import Quote, { QuoteContent, QuoteData } from './Quote';

export default function PlanContainer({
	consultingPackages,
	generalData,
	getLabel,
	id,
	onChangePlan,
	onNextBtnClick,
	onPlanSelect,
	onPrevBtnClick,
	plan,
	updatePackages,
}) {
	const { userData } = useContext(UserContext);

	const isLargeScreen = useMediaQuery({ query: '(min-width: 992px)' });

	// Determine what the default payment interval should be. Either 'monthly' or 'annually'.
	const [paymentInterval, setPaymentInterval] = useState(() => (plan.payment_interval !== '' ? plan.payment_interval : 'annually'));

	const previousPaymentInterval = useRef(paymentInterval);

	useEffect(() => {
		if (previousPaymentInterval.current !== paymentInterval) {
			gtmEventChangePaymentInterval({ paymentInterval, plan: id });
			previousPaymentInterval.current = paymentInterval;
		}
	}, [paymentInterval]);

	const getContentByStep = () => {
		switch (userData.step) {
			case 0:
				return <Overview id={id} data={plan} getLabel={getLabel} generalData={generalData} onPlanSelect={onPlanSelect} paymentInterval={paymentInterval} />;
			case 1:
				return (
					<Packages
						generalData={generalData}
						packages={consultingPackages}
						config={plan.consulting_packages}
						getLabel={getLabel}
						paymentInterval={paymentInterval}
						updatePackages={updatePackages}
					/>
				);
			case 2:
				return (
					<Quote>
						<QuoteData consultingPackages={consultingPackages} plan={plan} getLabel={getLabel} showPricePerUnit={generalData.show_price_per_unit} />
						{isLargeScreen && <QuoteContent generalData={generalData} getLabel={getLabel} />}
					</Quote>
				);
		}
	};

	const getExtraContainer = () => {
		if (!isLargeScreen && userData.step === 2) {
			return (
				<Container id={`plan-${id}-extra`} step={userData.step}>
					<div className="prc-content">
						<Quote>
							<QuoteContent generalData={generalData} getLabel={getLabel} />
						</Quote>
					</div>
				</Container>
			);
		}
	};

	return (
		<>
			<Container highlight={userData.step === 0 && plan.most_popular} id={`plan-${id}`} step={userData.step}>
				<Header
					heading={plan.name}
					sideContent={getHeaderContentByStep({ plan, step: userData.step, getLabel, onChangePlan, paymentInterval, setPaymentInterval })}
				/>
				<div className="prc-content">{getContentByStep()}</div>
				<Nav
					step={userData.step}
					getLabel={getLabel}
					onPrevBtnClick={onPrevBtnClick}
					onNextBtnClick={onNextBtnClick}
					canPurchase={userCanPurchase(generalData)}
					generalData={generalData}
				/>
			</Container>
			{getExtraContainer()}
		</>
	);
}

export function Container({ children, highlight = false, id = '', loading = false, step = 0 }) {
	const additionalAttributes = {};
	if (id) {
		additionalAttributes.id = id;
	}
	if (highlight) {
		additionalAttributes['data-color-scheme'] = 'highlight';
	}

	// Only add the test ID if the container is not loading.
	if (!loading) {
		additionalAttributes['data-testid'] = 'prc-container';
	}

	const classes = ['prc-container'];
	if (highlight) classes.push('prc-container--highlight');
	if (loading) classes.push('prc-container--loading');

	return (
		<div className={classes.join(' ')} data-step={step} {...additionalAttributes}>
			{children}
		</div>
	);
}

PlanContainer.propTypes = {
	consultingPackages: PropTypes.array.isRequired,
	generalData: PropTypes.object.isRequired,
	getLabel: PropTypes.func.isRequired,
	id: PropTypes.string.isRequired,
	onChangePlan: PropTypes.func.isRequired,
	onNextBtnClick: PropTypes.func.isRequired,
	onPlanSelect: PropTypes.func.isRequired,
	onPrevBtnClick: PropTypes.func.isRequired,
	plan: PropTypes.object.isRequired,
	updatePackages: PropTypes.func.isRequired,
};

Container.propTypes = {
	children: PropTypes.node.isRequired,
	highlight: PropTypes.bool,
	id: PropTypes.string,
	loading: PropTypes.bool,
	step: PropTypes.number,
};
